if (!customElements.get("rdm-liftoff-ingredients")) {
  customElements.define(
    "rdm-liftoff-ingredients",
    class extends HTMLElement {
      connectedCallback() {
        this.abort?.abort();
        this.abort = new AbortController();
        const { signal } = this.abort;
        this.buttons = [...this.querySelectorAll("[data-ingredient]")].sort(
          (a, b) => +a.dataset.index - +b.dataset.index
        );
        this.templates = new Map(
          [...this.querySelectorAll("template[data-content]")].map((el) => [
            el.dataset.content,
            el,
          ])
        );
        this.desktop = matchMedia("(min-width: 1000px)");
        this.active =
          this.buttons.find(
            (button) => button.getAttribute("aria-pressed") === "true"
          ) || this.buttons[0];
        this.desktop.addEventListener("change", () => this.arrange(), {
          signal,
        });
        this.addEventListener(
          "click",
          (event) => {
            const button = event.target.closest("[data-ingredient]");
            if (button && this.contains(button)) this.select(button);
            if (event.target.closest("[data-pause]")) {
              const paused = this.toggleAttribute("data-paused");
              this.querySelector("[data-pause]").setAttribute(
                "aria-pressed",
                String(paused)
              );
            }
          },
          { signal }
        );
        this.addEventListener(
          "keydown",
          (event) => {
            const button = event.target.closest("[data-ingredient]");
            if (
              !button ||
              ![
                "ArrowDown",
                "ArrowUp",
                "ArrowLeft",
                "ArrowRight",
                "Home",
                "End",
              ].includes(event.key)
            )
              return;
            event.preventDefault();
            const current = this.buttons.indexOf(button);
            const offset = ["ArrowUp", "ArrowLeft"].includes(event.key)
              ? -1
              : 1;
            const index =
              event.key === "Home"
                ? 0
                : event.key === "End"
                ? this.buttons.length - 1
                : (current + offset + this.buttons.length) %
                  this.buttons.length;
            this.select(this.buttons[index]);
            this.buttons[index].focus({ preventScroll: true });
          },
          { signal }
        );
        this.addEventListener("load", () => this.scheduleConnector(), {
          capture: true,
          signal,
        });
        document.addEventListener(
          "shopify:block:select",
          (event) => {
            const button = this.buttons.find(
              (item) => item.dataset.ingredient === event.detail.blockId
            );
            if (button) this.select(button);
            if (this.contains(event.target))
              this.setAttribute("data-editor-selected", "");
          },
          { signal }
        );
        document.addEventListener(
          "shopify:block:deselect",
          () => this.removeAttribute("data-editor-selected"),
          { signal }
        );
        this.observer?.disconnect();
        this.observer = new ResizeObserver(() => this.scheduleConnector());
        this.observer.observe(this);
        this.querySelectorAll(
          "[data-layout], [data-capsule], [data-panel]"
        ).forEach((el) => this.observer.observe(el));
        const group = this.querySelector("[data-badge-group]");
        if (group && !this.querySelector("[data-badge-copy]")) {
          const clone = group.cloneNode(true);
          clone.setAttribute("data-badge-copy", "");
          clone.setAttribute("aria-hidden", "true");
          clone.removeAttribute("data-badge-group");
          clone
            .querySelectorAll("[data-shopify-editor-block]")
            .forEach((el) => el.removeAttribute("data-shopify-editor-block"));
          group.after(clone);
        }
        this.arrange();
        if (this.active) this.select(this.active);
        document.fonts.ready.then(() => {
          if (this.isConnected) this.scheduleConnector();
        });
      }

      arrange() {
        if (!this.buttons.length) return;
        this.toggleAttribute("data-desktop", this.desktop.matches);
        const sorted = this.desktop.matches
          ? [...this.buttons].sort(
              (a, b) =>
                +a.dataset.order - +b.dataset.order ||
                +a.dataset.index - +b.dataset.index
            )
          : this.buttons;
        sorted.forEach((button) => {
          const column =
            this.desktop.matches &&
            ["left", "right", "center"].includes(button.dataset.columnSetting)
              ? button.dataset.columnSetting
              : "left";
          this.querySelector(`[data-column="${column}"]`).append(button);
        });
        this.scheduleConnector();
      }

      select(button) {
        const template = this.templates.get(button.dataset.ingredient);
        if (!template) return;
        this.active = button;
        this.buttons.forEach((item) =>
          item.setAttribute("aria-pressed", String(item === button))
        );
        const fragment = template.content.cloneNode(true);
        const title = fragment.querySelector("[data-title]");
        this.querySelector("[data-selected-title]").textContent =
          title.textContent;
        title.remove();
        [...fragment.childNodes]
          .filter(
            (node) =>
              node.nodeType === Node.TEXT_NODE && !node.textContent.trim()
          )
          .forEach((node) => node.remove());
        this.querySelector("[data-number]").textContent =
          button.dataset.index.padStart(2, "0");
        this.querySelector("[data-details]").replaceChildren(fragment);
        this.querySelector("[data-panel]").setAttribute(
          "aria-labelledby",
          button.id
        );
        this.scheduleConnector();
      }

      scheduleConnector() {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.drawConnector());
      }

      drawConnector() {
        const svg = this.querySelector("[data-connector]");
        const capsule = this.querySelector("[data-capsule]");
        if (!svg) return;
        svg.toggleAttribute("hidden", !capsule || !this.active);
        if (svg.hasAttribute("hidden")) return;
        const box = this.querySelector("[data-layout]").getBoundingClientRect();
        const card = this.active.getBoundingClientRect();
        const target = capsule.getBoundingClientRect();
        const below = this.active.parentElement.dataset.column === "center";
        const fromRight = card.left > target.left + target.width / 2;
        const inset = this.desktop.matches ? 15 : 8;
        const x1 =
          (below
            ? card.left + card.width / 2
            : fromRight
            ? card.left - inset
            : card.right + inset) - box.left;
        const y1 =
          (below ? card.top - inset : card.top + card.height / 2) - box.top;
        const x2 =
          (below
            ? target.left + target.width / 2
            : fromRight
            ? target.right
            : target.left) - box.left;
        const y2 =
          (below ? target.bottom : target.top + target.height * 0.32) - box.top;
        const bend =
          x1 + Math.sign(x2 - x1) * Math.min(34, Math.abs(x2 - x1) * 0.5);
        svg.setAttribute("viewBox", `0 0 ${box.width} ${box.height}`);
        svg
          .querySelector("path")
          .setAttribute(
            "d",
            below
              ? `M ${x1} ${y1} V ${y2} H ${x2}`
              : `M ${x1} ${y1} H ${bend} L ${x2} ${y2}`
          );
        svg.querySelector("circle").setAttribute("cx", x1);
        svg.querySelector("circle").setAttribute("cy", y1);
      }

      disconnectedCallback() {
        this.abort?.abort();
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
      }
    }
  );
}
